import { useLazyQuery } from "@apollo/client";
import {
  Badge,
  Box,
  Button,
  Flex,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";

import React, { useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import HeaderWithActions from "../../components/HeaderWithActions";
import ListView from "../../components/ListView";
import Meta from "../../components/Meta";
import { useSchemaQuery } from "../../react-apollo-introspection";
import debounce from "lodash.debounce";
import { DELETE, FETCH_ALL } from "./graphql";
import { setup } from "./setup";
import { useRecoilState } from "recoil";
import { tableAtom } from "../../components/ListView/atoms";

const columns = [
  {
    Header: "",
    accessor: "_id",
    disableSortBy: true,
    Cell: ({ row }) => (
      <Link to={`/${setup.model}/edit/${row.original[setup.accessor]}`}>
        <Button variant="link">View</Button>
      </Link>
    ),
  },
  {
    Header: "Name",
    accessor: "title",
    disableSortBy: false,
    Cell: ({ value }) => <div>{value}</div>,
  },
];

const filters = [
  {
    value: "last_name",
    type: "text",
    label: "Last Name",
    isOperator: false,
  },
];

const List = ({ title }) => {
  const [fetchData, { loading, error, data }] = useSchemaQuery(FETCH_ALL);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchString, setSearchString] = useState(
    searchParams.get("searchName") || ""
  );
  const [tableState, setTableState] = useRecoilState(tableAtom);

  const updateFiltersSafely = (updateFn) => {
    setTableState((old) => ({ ...old, hydrated: "" }));
    searchParams.set("page", "1");
    searchParams.set("limit", "20");
    setTimeout(() => {
      updateFn();
      setTableState((old) => ({
        ...old,
        params: {
          ...old.params,
          page: 1,
          skip: 0,
          pagination: 20,
        },
        hydrated: setup.model,
      }));
    }, 0);
  };

  const delayedSetSearchParams = (params) => {
    setTimeout(() => setSearchParams(params), 0);
  };

  const confirm = (val) => {
    updateFiltersSafely(() => {
      setSearchString(val);
      if (val) {
        searchParams.set("searchName", val);
      } else {
        searchParams.delete("searchName");
      }
      delayedSetSearchParams(searchParams);
    });
  };

  const buildFetchFilters = () => {
    const filter = { type: "location" };
    if (searchString) filter.title = searchString;
    return { filter };
  };

  const _searchFunction = useCallback(debounce(confirm, 500), [searchString]);

  const fetchFilters = useMemo(() => {
    return buildFetchFilters();
  }, [searchString]);

  return (
    <Meta title={setup.title}>
      <HeaderWithActions title={setup.title}>
        <Stack ml="auto" isInline spacing="15px">
          {setup.canAddNew && (
            <Link to={`/${setup.model}/create`}>
              <Button>Add {setup.singular}</Button>
            </Link>
          )}
        </Stack>
      </HeaderWithActions>
      <Box
        mt="25px"
        bg={useColorModeValue("white", "gray.700")}
        shadow="base"
        rounded={{
          base: "md",
          sm: "xl",
        }}
        w="100%"
      >
        <ListView
          setup={setup}
          fetchData={fetchData}
          data={data?.[FETCH_ALL]}
          fetchFilters={fetchFilters}
          loading={loading}
          error={error}
          gqlDelete={DELETE}
          columns={columns}
          filters={filters}
          setSearchString={_searchFunction}
          searchString={searchString}
        />
      </Box>
    </Meta>
  );
};

export default List;
